// Поиск в top-bar: задачи (с сервера) и агенты (из справочника). ⌘K / Ctrl+K — фокус.
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { api, isAbort, qs } from '../api.ts'
import { useDebouncedValue } from '../lib/hooks.ts'
import { navigate, openTask } from '../router.tsx'
import { useLive } from '../store.ts'
import type { Agent, TaskCard } from '../types.ts'
import { AgentAvatar } from './AgentAvatar.tsx'
import { StatusBadge } from './chips.tsx'
import { Icon } from './Icon.tsx'
import { cx } from './ui.tsx'

type Item = { kind: 'task'; id: number; task?: TaskCard; label: string } | { kind: 'agent'; agent: Agent }

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

/** autoFocus и onDone — для поля поверх top-bar на телефоне: открылось с фокусом, закрывается после выбора, Esc и касания мимо. */
export function GlobalSearch({ autoFocus, onDone }: { autoFocus?: boolean; onDone?: () => void } = {}) {
  const agents = useLive((s) => s.agents)
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [tasks, setTasks] = useState<TaskCard[]>([])
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const listId = useId()
  const query = useDebouncedValue(q.trim(), 200)

  // ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
        setOpen(true)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // клик мимо — закрыть
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (boxRef.current?.contains(e.target as Node)) return
      setOpen(false)
      onDone?.()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open, onDone])

  useEffect(() => {
    if (!query) {
      setTasks([])
      return
    }
    const ctrl = new AbortController()
    setLoading(true)
    api
      .get<TaskCard[]>(`/api/tasks${qs({ q: query })}`, { signal: ctrl.signal })
      .then((r) => setTasks(r.slice(0, 8)))
      .catch((e: unknown) => {
        if (!isAbort(e)) setTasks([])
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false)
      })
    return () => ctrl.abort()
  }, [query])

  const items = useMemo<Item[]>(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return []
    const out: Item[] = []
    const keyMatch = /^(?:oma-)?(\d{1,7})$/i.exec(needle)
    if (keyMatch && !tasks.some((t) => t.id === Number(keyMatch[1]))) {
      out.push({ kind: 'task', id: Number(keyMatch[1]), label: `Открыть OMA-${keyMatch[1]}` })
    }
    for (const t of tasks) out.push({ kind: 'task', id: t.id, task: t, label: t.title })
    const ag = agents
      .filter(
        (a) =>
          !a.archived &&
          (a.name.toLowerCase().includes(needle) || a.id.includes(needle) || a.title.toLowerCase().includes(needle)),
      )
      .slice(0, 6)
    for (const a of ag) out.push({ kind: 'agent', agent: a })
    return out
  }, [q, tasks, agents])

  useEffect(() => {
    setActive(0)
  }, [items.length, q])

  const choose = (it: Item | undefined) => {
    if (!it) return
    setOpen(false)
    setQ('')
    inputRef.current?.blur()
    onDone?.()
    if (it.kind === 'task') openTask(it.id)
    else navigate(`/agents/${encodeURIComponent(it.agent.id)}`)
  }

  const taskItems = items.filter((i): i is Extract<Item, { kind: 'task' }> => i.kind === 'task')
  const agentItems = items.filter((i): i is Extract<Item, { kind: 'agent' }> => i.kind === 'agent')
  const showList = open && q.trim().length > 0

  return (
    <div className="search" ref={boxRef}>
      <div className="box">
        <Icon name="search" size={15} />
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          type="search"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-label="Поиск задач и агентов"
          aria-activedescendant={showList && items[active] ? `${listId}-${active}` : undefined}
          placeholder="Задача, OMA-12, агент…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setActive((i) => Math.min(i + 1, Math.max(items.length - 1, 0)))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setActive((i) => Math.max(i - 1, 0))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              choose(items[active])
            } else if (e.key === 'Escape') {
              e.stopPropagation()
              setOpen(false)
              inputRef.current?.blur()
              onDone?.()
            }
          }}
        />
        {loading ? <Icon name="loader" size={14} className="animate-spin" /> : <span className="k">{isMac ? '⌘K' : 'Ctrl K'}</span>}
      </div>
      {showList ? (
        <div
          id={listId}
          role="listbox"
          aria-label="Результаты поиска"
          className="anim-fade-in absolute top-[42px] right-0 left-0 z-50 max-h-[70vh] overflow-y-auto rounded-card border border-line-strong bg-surface p-1.5 shadow-float"
        >
          {items.length === 0 ? (
            <div className="px-3 py-4 text-center text-[13px] text-muted">{loading ? 'Ищу…' : 'Ничего не нашлось'}</div>
          ) : null}
          {taskItems.length ? <div className="lbl px-2.5 pt-1.5 pb-1">Задачи</div> : null}
          {taskItems.map((it) => {
            const idx = items.indexOf(it)
            return (
              <button
                key={`t${it.id}`}
                id={`${listId}-${idx}`}
                type="button"
                role="option"
                aria-selected={idx === active}
                onMouseEnter={() => setActive(idx)}
                onClick={() => choose(it)}
                className={cx(
                  'flex w-full min-w-0 items-center gap-2.5 rounded-ctl px-2.5 py-2 text-left text-[13px]',
                  idx === active ? 'bg-surface-2' : '',
                )}
              >
                <span className="tnum w-[58px] shrink-0 font-mono text-[11.5px] text-subtle">OMA-{it.id}</span>
                <span className="min-w-0 flex-1 truncate">{it.label}</span>
                {it.task ? <StatusBadge status={it.task.status} /> : null}
              </button>
            )
          })}
          {agentItems.length ? <div className="lbl px-2.5 pt-2.5 pb-1">Агенты</div> : null}
          {agentItems.map((it) => {
            const idx = items.indexOf(it)
            return (
              <button
                key={`a${it.agent.id}`}
                id={`${listId}-${idx}`}
                type="button"
                role="option"
                aria-selected={idx === active}
                onMouseEnter={() => setActive(idx)}
                onClick={() => choose(it)}
                className={cx(
                  'flex w-full min-w-0 items-center gap-2.5 rounded-ctl px-2.5 py-2 text-left text-[13px]',
                  idx === active ? 'bg-surface-2' : '',
                )}
              >
                <AgentAvatar agent={it.agent} size="sm" />
                <span className="min-w-0 truncate font-medium">{it.agent.name}</span>
                <span className="min-w-0 flex-1 truncate text-[12px] text-muted">{it.agent.title}</span>
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
